/**
 * TanStack Query Client Configuration
 * Optimized configuration for news application with enhanced error handling
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

// Default stale time for different types of data
export const STALE_TIME = {
  // News data becomes stale after 2 minutes
  NEWS: 2 * 60 * 1000,
  // Categories and static data becomes stale after 30 minutes
  STATIC: 30 * 60 * 1000,
  // User-specific data becomes stale after 5 minutes
  USER: 5 * 60 * 1000,
  // Search results become stale after 1 minute
  SEARCH: 1 * 60 * 1000,
} as const;

// Garbage collection time (how long to keep data in cache after it becomes unused)
export const GC_TIME = {
  NEWS: 10 * 60 * 1000, // 10 minutes
  STATIC: 60 * 60 * 1000, // 1 hour
  USER: 15 * 60 * 1000, // 15 minutes
  SEARCH: 5 * 60 * 1000, // 5 minutes
} as const;

// Network status detection
const isOnline = () => {
  if (typeof navigator !== 'undefined') {
    return navigator.onLine;
  }
  return true;
};

// Enhanced error handling function
const handleQueryError = (error: unknown, query: any) => {
  console.group(`🚨 Query Error: ${query.queryHash}`);
  console.error('Query Key:', query.queryKey);
  console.error('Error:', error);

  if (error instanceof Error) {
    // Specific error handling for different types
    if (error.message.includes('503')) {
      console.warn('Service temporarily unavailable - data may be served from cache');
    } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      console.warn('Network error detected - check internet connection');
    } else if (error.message.includes('timeout')) {
      console.warn('Request timeout - server may be experiencing high load');
    }
  }

  console.groupEnd();

  // Send error to monitoring service in production
  if (process.env.NODE_ENV === 'production') {
    // TODO: Integrate with error monitoring service (e.g., Sentry)
    // sendErrorToMonitoring(error, query);
  }
};

// Enhanced mutation error handling
const handleMutationError = (error: unknown, variables: any, context: any, mutation: any) => {
  console.group(`🚨 Mutation Error: ${mutation.mutationId}`);
  console.error('Variables:', variables);
  console.error('Error:', error);
  console.groupEnd();

  // Send mutation errors to monitoring service
  if (process.env.NODE_ENV === 'production') {
    // TODO: Integrate with error monitoring service
    // sendMutationErrorToMonitoring(error, variables, context, mutation);
  }
};

// Create query cache with global error handling
const queryCache = new QueryCache({
  onError: handleQueryError,
  onSuccess: (data, query) => {
    // Log successful queries in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Query Success: ${query.queryHash}`, {
        queryKey: query.queryKey,
        dataLength: Array.isArray(data) ? data.length : 'N/A'
      });
    }
  },
});

// Create mutation cache with global error handling
const mutationCache = new MutationCache({
  onError: handleMutationError,
  onSuccess: (data, variables, context, mutation) => {
    // Log successful mutations in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Mutation Success: ${mutation.mutationId}`, {
        variables,
        data
      });
    }
  },
});

// Smart retry function with exponential backoff
const smartRetry = (failureCount: number, error: any) => {
  // Don't retry if offline
  if (!isOnline()) {
    return false;
  }

  // Don't retry client errors (4xx)
  if (error?.status >= 400 && error?.status < 500) {
    return false;
  }

  // Don't retry after 3 attempts
  if (failureCount >= 3) {
    return false;
  }

  // Don't retry certain error types
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('not found') ||
      message.includes('unauthorized') ||
      message.includes('forbidden')
    ) {
      return false;
    }
  }

  return true;
};

// Smart retry delay with exponential backoff and jitter
const smartRetryDelay = (attemptIndex: number) => {
  // Base delay: 1000ms
  const baseDelay = 1000;
  // Exponential backoff with jitter to prevent thundering herd
  const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);
  const jitter = Math.random() * 1000;
  const maxDelay = 30000; // Max 30 seconds

  return Math.min(exponentialDelay + jitter, maxDelay);
};

// Create and configure the query client
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Default stale time (2 minutes for news data)
      staleTime: STALE_TIME.NEWS,

      // Default garbage collection time (10 minutes)
      gcTime: GC_TIME.NEWS,

      // Smart retry logic
      retry: smartRetry,

      // Smart retry delay
      retryDelay: smartRetryDelay,

      // Refetch behavior
      refetchOnWindowFocus: false, // Disabled for better performance
      refetchOnReconnect: true,    // Refetch when reconnecting
      refetchOnMount: false,       // Don't refetch if data is fresh

      // Network mode
      networkMode: 'online',       // Only fetch when online

      // Error handling
      throwOnError: false,         // Don't throw errors by default

      // Structural sharing (optimize re-renders)
      structuralSharing: true,

      // Default select function to ensure data is always an array for news queries
      select: (data: any) => {
        if (data?.data && Array.isArray(data.data)) {
          return data.data;
        }
        if (Array.isArray(data)) {
          return data;
        }
        return data;
      },
    },
    mutations: {
      // Smart retry for mutations (more conservative)
      retry: (failureCount: number, error: any) => {
        // Only retry mutations once
        if (failureCount >= 1) return false;

        // Don't retry client errors
        if (error?.status >= 400 && error?.status < 500) return false;

        return true;
      },

      // Retry delay for mutations
      retryDelay: 2000, // 2 seconds

      // Network mode
      networkMode: 'online',
    },
  },
});

// Query key factories for consistent caching
export const queryKeys = {
  // News-related query keys
  news: {
    all: ['news'] as const,
    lists: () => [...queryKeys.news.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.news.lists(), { filters }] as const,
    details: () => [...queryKeys.news.all, 'detail'] as const,
    detail: (slug: string) => [...queryKeys.news.details(), slug] as const,

    // Specific news types
    latest: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'latest', { location, limit }] as const,
    popular: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'popular', { location, limit }] as const,
    hot: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'hot', { location, limit }] as const,
    viral: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'viral', { location, limit }] as const,
    celebrity: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'celebrity', { location, limit }] as const,
    mostSee: (location?: string, limit?: number) =>
      [...queryKeys.news.lists(), 'mostSee', { location, limit }] as const,

    // Category and tag queries
    byCategory: (categorySlug: string, page?: number, location?: string) =>
      [...queryKeys.news.lists(), 'category', categorySlug, { page, location }] as const,
    byTag: (tagSlug: string, page?: number, location?: string) =>
      [...queryKeys.news.lists(), 'tag', tagSlug, { page, location }] as const,

    // Search queries
    search: (term: string, page?: number, location?: string) =>
      [...queryKeys.news.lists(), 'search', term, { page, location }] as const,
  },

  // Categories
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.categories.lists(), { filters }] as const,
  },

  // Tags
  tags: {
    all: ['tags'] as const,
    lists: () => [...queryKeys.tags.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.tags.lists(), { filters }] as const,
  },

  // Locations
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.locations.lists(), { filters }] as const,
  },
} as const;

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate all news queries
  invalidateNews: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.news.all });
  },

  // Invalidate specific news type
  invalidateNewsType: (type: string) => {
    return queryClient.invalidateQueries({
      queryKey: [...queryKeys.news.lists(), type]
    });
  },

  // Invalidate news by category
  invalidateNewsByCategory: (categorySlug: string) => {
    return queryClient.invalidateQueries({
      queryKey: [...queryKeys.news.lists(), 'category', categorySlug]
    });
  },

  // Prefetch news data
  prefetchNews: async (type: string, params: Record<string, any> = {}) => {
    const queryKey = [...queryKeys.news.lists(), type, params];

    return queryClient.prefetchQuery({
      queryKey,
      staleTime: STALE_TIME.NEWS,
      gcTime: GC_TIME.NEWS,
    });
  },

  // Get cached news data without triggering a fetch
  getCachedNews: (type: string, params: Record<string, any> = {}) => {
    const queryKey = [...queryKeys.news.lists(), type, params];
    return queryClient.getQueryData(queryKey);
  },

  // Clear all cache
  clearCache: () => {
    return queryClient.clear();
  },

  // Get cache statistics
  getCacheStats: () => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();

    return {
      totalQueries: queries.length,
      successfulQueries: queries.filter(q => q.state.status === 'success').length,
      errorQueries: queries.filter(q => q.state.status === 'error').length,
      loadingQueries: queries.filter(q => q.state.status === 'pending').length,
      cacheSize: JSON.stringify(queries).length,
    };
  },
};

// Network status hook utilities
export const networkUtils = {
  // Check if online
  isOnline,

  // Add network status listeners
  addNetworkListeners: () => {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        queryClient.resumePausedMutations();
        queryClient.invalidateQueries();
      });

      window.addEventListener('offline', () => {
        // Pause queries when offline
        console.log('📱 Device went offline - queries will be paused');
      });
    }
  },

  // Remove network status listeners
  removeNetworkListeners: () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', () => {});
      window.removeEventListener('offline', () => {});
    }
  },
};

// Export configured query client as default
export default queryClient;
